// TYPES
import { useMemo } from 'react';
import { OrganizationNodesAndRoles } from 'components/OrganizationNodeAndRoles';
import StandardOrganizationSelector from 'components/SelectComponents/StandardOrganizationNodeSelector';
// i18N
import intl from 'i18n/intl';
import _ from 'lodash';
import { EAppId } from '@timeedit/types/lib/enums';
import { EOrganizationVisibility } from '@timeedit/types/lib/enums/organizationVisibility.enum';
import type { TLanguage } from '@timeedit/types/lib/types';
import { TEInput } from '@timeedit/ui-components';
import { Form, Typography } from '@timeedit/ui-components/antd';

const language: any = intl.messages;

interface GeneralSettingsFormProps {
  organization: any;
  isEditable: boolean;
  allLanguages: TLanguage[];
  onChange?: (prop: string, value: any) => void;
}

export const GeneralSettingsForm = ({
  organization,
  isEditable,
  allLanguages,
  onChange,
}: GeneralSettingsFormProps) => {
  const [form] = Form.useForm();

  const appIdOptions = useMemo(
    () =>
      Object.values(EAppId).map((value) => ({
        value,
        label: `TE ${_.startCase(
          _.lowerCase(value.replace('TE_', '').replaceAll('_', ' '))
        )}`,
      })),
    []
  );

  const languageOptions = useMemo(
    () =>
      allLanguages.map((lang: TLanguage) => ({
        value: lang.id,
        label: lang.name,
      })),
    [allLanguages]
  );

  // Helpers for ReadOnly display
  const getLanguageName = (id: string) =>
    allLanguages.find((l) => l.id === id)?.name || 'Not set';

  const getVisibilityLabel = (vis: EOrganizationVisibility) => {
    switch (vis) {
      case EOrganizationVisibility.PUBLIC:
        return language.org_visibility_public;
      case EOrganizationVisibility.HIDDEN:
        return language.org_visibility_hidden;
      case EOrganizationVisibility.PRIVATE:
        return language.org_visibility_private;
      default:
        return 'Not set';
    }
  };

  const handleChange = (prop: string, value: any) => {
    if (onChange) {
      onChange(prop, value);
    }
  };

  return (
    <Form layout="vertical" form={form} style={{ padding: '15px' }}>
      <Typography.Title level={4}>Organisation details</Typography.Title>

      <Form.Item>
        {isEditable ? (
          <TEInput.Input
            label={language.name}
            value={organization.name || undefined}
            onChange={(val) => handleChange('name', val)}
            tooltip={language.name}
          />
        ) : (
          <TEInput.PureText
            label={language.name}
            value={organization.name || undefined}
            tooltip={language.name}
          />
        )}
      </Form.Item>

      <Form.Item>
        {isEditable ? (
          <TEInput.Input
            label="Logo"
            value={organization.customerLogo || null}
            onChange={(val) =>
              handleChange('customerLogo', val ? encodeURI(val) : null)
            }
            tooltip="Customer logo. URL to image."
          />
        ) : (
          <TEInput.PureText
            label="Logo"
            value={organization.customerLogo || null}
            tooltip="Customer logo. URL to image."
          />
        )}
      </Form.Item>

      <Form.Item>
        {isEditable ? (
          <TEInput.Select
            label="Default language"
            tooltip="The language that will be used as default for the organization"
            value={organization.language}
            options={languageOptions}
            onChange={(val) => handleChange('language', val)}
          />
        ) : (
          <TEInput.PureText
            label="Default language"
            tooltip="The language that will be used as default for the organization"
            value={getLanguageName(organization.language)}
          />
        )}
      </Form.Item>

      <Form.Item>
        {isEditable ? (
          <TEInput.Select
            label="Default date format"
            tooltip="The date format that will be used as default for the organization"
            value={organization.dateFormat}
            options={languageOptions}
            onChange={(val) => handleChange('dateFormat', val)}
          />
        ) : (
          <TEInput.PureText
            label="Default date format"
            tooltip="The date format that will be used as default for the organization"
            value={getLanguageName(organization.dateFormat)}
          />
        )}
      </Form.Item>

      <Form.Item>
        {isEditable ? (
          <TEInput.Select
            label="Visibility"
            tooltip="Choose how to show the organization in the login page"
            value={organization.visibility}
            onChange={(value: EOrganizationVisibility) =>
              handleChange('visibility', value)
            }
            options={[
              {
                value: EOrganizationVisibility.PUBLIC,
                label: language.org_visibility_public,
              },
              {
                value: EOrganizationVisibility.HIDDEN,
                label: language.org_visibility_hidden,
              },
              {
                value: EOrganizationVisibility.PRIVATE,
                label: language.org_visibility_private,
              },
            ]}
          />
        ) : (
          <TEInput.PureText
            label="Visibility"
            tooltip="Choose how to show the organization in the login page"
            value={getVisibilityLabel(organization.visibility)}
          />
        )}
      </Form.Item>

      <Form.Item>
        <TEInput.PureText label="ID" tooltip="ID" value={organization.id} />
      </Form.Item>

      <Form.Item>
        <TEInput.PureText
          label="Customer signature"
          tooltip="Customer signature"
          value={organization.customerSignature}
        />
      </Form.Item>

      <Form.Item>
        <TEInput.PureText
          label="Region"
          tooltip="Region"
          value={organization.region || 'Nothing'}
        />
      </Form.Item>

      <Form.Item>
        <div>
          <TEInput.PureText
            label="Enabled applications"
            tooltip="Enabled applications"
            value={''}
          />
          <TEInput.Select
            mode="multiple"
            showSearch
            size="small"
            disabled={!isEditable}
            filterOption={(input: any, option: any) =>
              option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            value={organization.enabledProducts}
            onChange={(enabledProducts: string[]) =>
              handleChange('enabledProducts', enabledProducts)
            }
            options={appIdOptions}
          />
        </div>
      </Form.Item>
      <br />

      {isEditable && (
        <>
          <Typography.Title level={4}>Root user configuration</Typography.Title>
          <Form.Item>
            <StandardOrganizationSelector
              selectedStandardOrganizationId={
                organization.rootUserStandardOrganizationNode
              }
              onChange={(val) =>
                handleChange('rootUserStandardOrganizationNode', val)
              }
            />
          </Form.Item>
          <div
            className="org-nodes-and-roles"
            data-testid="organization-nodes-and-roles"
          >
            <OrganizationNodesAndRoles
              title="Root user permissions"
              tooltip="Root user permissions"
              organizationNodesAndRoles={organization.rootUserPermissions}
              onChange={(rootUserPermissions) =>
                handleChange('rootUserPermissions', rootUserPermissions)
              }
              key={organization.id}
            />
          </div>
        </>
      )}
    </Form>
  );
};
